import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import { scheduleLabel } from '@/utils/bulkPayments';
import type { BulkSchedule } from '@/types/portal';

interface ConfirmStepProps {
  recipientCount: number;
  total: number;
  currency: string;
  walletName: string;
  schedule: BulkSchedule;
  otp: string;
  onOtp: (value: string) => void;
  onVerify: () => void;
  onCancel: () => void;
  canApprove: boolean;
}

export default function ConfirmStep({
  recipientCount,
  total,
  currency,
  walletName,
  schedule,
  otp,
  onOtp,
  onVerify,
  onCancel,
  canApprove,
}: ConfirmStepProps) {
  const ready = otp.length === 6;
  const scheduled = schedule.mode === 'scheduled';
  const verifyLabel = !canApprove
    ? 'Verify and Submit'
    : scheduled
      ? 'Verify and Schedule'
      : 'Verify and Complete';

  return (
    <div className="mx-auto mt-6 w-full max-w-4xl">
      <div className="flex justify-end">
        <Button variant="outline" icon="ri-close-line" onClick={onCancel}>
          Cancel
        </Button>
      </div>

      <section className="mt-4 rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-col items-center px-6 py-10 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-2xl text-primary-700">
            <i className="ri-shield-keyhole-line" />
          </span>
          <h3 className="mt-4 font-heading text-xl font-semibold text-foreground-950">
            OTP Verification Required
          </h3>
          <p className="mt-1 text-sm text-foreground-600">
            Enter the 6-digit code sent to your phone to authorise this bulk transfer.
          </p>

          <div className="mt-6 w-full max-w-md text-left">
            <label htmlFor="bulk-otp" className="text-xs font-label font-medium text-foreground-600">
              Enter OTP Code
            </label>
            <input
              id="bulk-otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              onChange={(event) => onOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="mt-1.5 h-14 w-full rounded-md border border-background-300 bg-background-50 text-center font-heading text-2xl tracking-[0.6em] text-foreground-900 placeholder:text-foreground-300 focus:border-primary-400 focus:outline-none"
            />
            <p className="mt-1.5 text-xs text-foreground-500">
              Enter the 6-digit verification code
            </p>
          </div>

          <div className="mt-6 w-full max-w-md">
            <Button
              icon="ri-checkbox-circle-line"
              className="w-full"
              onClick={onVerify}
              disabled={!ready}
            >
              {verifyLabel}
            </Button>
          </div>
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-background-200 bg-background-50 px-6 py-5">
        <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
          <i className="ri-file-list-3-line text-foreground-500" />
          Transaction Summary
        </h4>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs text-foreground-500">Transactions</p>
            <p className="mt-1 text-lg font-semibold text-foreground-950">{recipientCount}</p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">Total Amount</p>
            <p className="mt-1 text-lg font-semibold text-primary-700">
              {formatMoney(total, currency)}
            </p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">From Wallet</p>
            <p className="mt-1 truncate text-lg font-semibold text-foreground-950">
              {walletName || '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-foreground-500">Payment timing</p>
            <p
              className={`mt-1 inline-flex items-center gap-1.5 text-sm font-semibold ${
                scheduled ? 'text-secondary-900' : 'text-accent-700'
              }`}
            >
              <i className={scheduled ? 'ri-calendar-schedule-line' : 'ri-flashlight-line'} />
              {scheduleLabel(schedule)}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}